import React from 'react';
import {
  MedicineBoxOutlined,
  HeartOutlined,
  BankOutlined,
  VideoCameraOutlined,
  ExperimentOutlined,
  SafetyCertificateOutlined,
  AppstoreOutlined,
  StarOutlined,
  TeamOutlined,
  FileProtectOutlined,
  SafetyOutlined,
  UserOutlined,
  GlobalOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  PhoneOutlined,
  FileTextOutlined,
  SmileOutlined,
  CompassOutlined,
  CustomerServiceOutlined,
  ReconciliationOutlined,
  CrownOutlined,
  ThunderboltOutlined,
  TrophyOutlined,
  SecurityScanOutlined,
  AuditOutlined,
  AlertOutlined,
  LikeOutlined,
  SkinOutlined,
  ZoomInOutlined,
  WomanOutlined,
  ManOutlined,
  FireOutlined,
  BulbOutlined,
  ApartmentOutlined,
  ShareAltOutlined,
  EyeOutlined,
  AudioOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  DashboardOutlined,
  CoffeeOutlined,
  RestOutlined,
  BugOutlined,
  ScissorOutlined,
  PlusSquareOutlined,
  HomeOutlined,
  ShopOutlined,
  LineChartOutlined,
  AimOutlined,
} from '@ant-design/icons';


// ─── Type-based routing ────────────────────────────────────────────────────────
// Routes are determined by category.type string, NOT by product_category_id
// Any category that is not GP (direct, no product list) and not Video (external URL) is expandable.
// This covers d2c, corporate, and any future types without code changes.
export const isExpandable = (cat) => cat?.type !== 'gp' && cat?.type !== 'video';
export const isDirect     = (cat) => cat?.type === 'gp';
export const isExternal   = (cat) => cat?.type === 'video';

export const EXTERNAL_URL = 'https://ldc-online-booking.semble.io/?token=2a453adcfcb727cfd1e91f3e245edcd360931ec3';

export const PRODUCTS_INITIAL_SHOW = 6;

// ─── Icons ─────────────────────────────────────────────────────────────────────
// icon_name is the string stored in the category.icon_name field from the API
const ICON_COMPONENT_MAP = {
  MedicineBoxOutlined,
  HeartOutlined,
  BankOutlined,
  VideoCameraOutlined,
  ExperimentOutlined,
  SafetyCertificateOutlined,
  StarOutlined,
  TeamOutlined,
  FileProtectOutlined,
  AppstoreOutlined,
  SafetyOutlined,
  UserOutlined,
  GlobalOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  PhoneOutlined,
  FileTextOutlined,
  SmileOutlined,
  CompassOutlined,
  CustomerServiceOutlined,
  ReconciliationOutlined,
  CrownOutlined,
  ThunderboltOutlined,
  TrophyOutlined,
  SecurityScanOutlined,
  AuditOutlined,
  AlertOutlined,
  LikeOutlined,
  SkinOutlined,
  ZoomInOutlined,
  WomanOutlined,
  ManOutlined,
  FireOutlined,
  BulbOutlined,
  ApartmentOutlined,
  ShareAltOutlined,
  EyeOutlined,
  AudioOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  DashboardOutlined,
  CoffeeOutlined,
  RestOutlined,
  BugOutlined,
  ScissorOutlined,
  PlusSquareOutlined,
  HomeOutlined,
  ShopOutlined,
  LineChartOutlined,
  AimOutlined,
};
// Fallback by type when icon_name is absent
const TYPE_ICON_FALLBACK = {
  gp:        MedicineBoxOutlined,
  d2c:       HeartOutlined,
  corporate: BankOutlined,
  video:     VideoCameraOutlined,
};

export const getCategoryIcon = (iconName, type) => {
  const ByName = ICON_COMPONENT_MAP[iconName];
  const ByType = TYPE_ICON_FALLBACK[type];
  const Icon   = ByName || ByType || AppstoreOutlined;
  return <Icon />;
};

// ─── Utilities ─────────────────────────────────────────────────────────────────

/** Strip React elements from an object before passing to callbacks */
export const sanitizeForCallback = (obj) => {
  if (obj === null || obj === undefined) return obj;
  if (React.isValidElement(obj)) return undefined;
  if (Array.isArray(obj)) return obj.map(sanitizeForCallback);
  if (typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj)
        .map(([k, v]) => [k, sanitizeForCallback(v)])
        .filter(([, v]) => v !== undefined)
    );
  }
  return obj;
};

/** Get sorted, deduplicated, active products for a category from the productsMap */
export const getProductsForCategory = (productsMap, categoryId) => {
  const list = productsMap[categoryId] || [];
  const seen = new Set();
  return list
    .filter((p) => {
      if (p.status !== undefined && p.status != 1) return false;
      if (seen.has(p.non_gp_product_id)) return false;
      seen.add(p.non_gp_product_id);
      return true;
    })
    .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999) || (a.name || '').localeCompare(b.name || ''));
};

/** Strip HTML tags to plain text */
export const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').trim();
};

/** Read and sort categories from localStorage.showCategory */
export const buildCategoriesFromStorage = () => {
  try {
    const raw = localStorage.getItem('showCategory');
    if (!raw) return [];
    const catMap = JSON.parse(raw);
    return Object.values(catMap)
      .filter(c => c?.product_category_id)
      .sort((a, b) => (a.sort_order ?? 9999) - (b.sort_order ?? 9999));
  } catch {
    return [];
  }
};
